import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

// Extend dayjs with required plugins
dayjs.extend(utc)
dayjs.extend(timezone)

// ReactQuill CSS removed - using TipTap editor instead
import { withAsyncPaginate } from 'react-select-async-paginate'
import Creatable from 'react-select/creatable'
import { RectangleRadioButton } from 'src/clinic/components/ClinicManageBookingsPage/ClinicManageBookingComponents'
import { getCPDataWithoutParams } from 'src/clinic/services'
import { GENDER_LIST_ONBOARDING } from 'src/consumer/constants'
import { getAgeFromDobWithTime } from 'src/consumer/helpers/profilePageHelper'
import { getParseDataFromHtmlString, isValidNumber, getExactDiffFromCurrentTimeDayJs } from 'src/consumer/helpers/utilFunctions'
import { getTherapyReportLabelFromEntity } from './therapyReportLabels'
import {
  PMPFooterContainer,
  PMPHeaderDetails,
  PMPHeadingBanner,
  PMPSectionInputBox,
  PMPSectionInputLabel,
  PMPSectionTextAreaBox,
} from '../PrescriptionMakingPage/PrescriptionMakingPageStyles'
import { styled } from '@mui/material/styles'
import { PrescriptionWrapperForRichText } from '../ScreeningReportMakingPage/ScreeningReportMakingPageComponents'
import TherapyReportPreview from 'src/mentor/components/TherapyReportMakingPage/components/TherapyReportPreview'
import { calculateNextVistDateInMoment } from 'src/mentor/components/PrescriptionMakingPage/PrescriptionMakingPageComponents'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import VideocamIcon from '@mui/icons-material/Videocam'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import AccessTime from '@mui/icons-material/AccessTime'
import { BOOKED_EVENT_MODES, displayNamesOfEventTypes } from 'src/consumer/constants'
import { getAdditionalTherapyTypesForSubmission } from './therapyReportUtils'

/**
 * Parse a report timestamp the same way as therapy report: string in 'YYYY-MM-DD HH:mm:ss' (UTC)
 * or number (Unix seconds if < 1e12, else milliseconds). Use for display and sort.
 */
export function parseReportTimestamp(value) {
  if (value == null || value === '') return null
  if (typeof value === 'number') {
    return value < 1e12 ? dayjs.unix(value) : dayjs(value)
  }
  return dayjs.utc(value, 'YYYY-MM-DD HH:mm:ss')
}

export function getDateOfTherapyReport(therapyReportData, withTime) {
  let format = 'DD/MM/YYYY'
  if (withTime) {
    format = 'DD/MM/YYYY h:mmA'
  }

  if (therapyReportData?.createdTime || therapyReportData?.modifiedTime) {
    return dayjs
      .utc(therapyReportData?.createdTime || therapyReportData?.modifiedTime, 'YYYY-MM-DD HH:mm:ss')
      .local()
      .format(format)
  }
  return dayjs().format(format)
}

/**
 * Format assessment report date using the same parsing as therapy report (YYYY-MM-DD HH:mm:ss or Unix seconds).
 * Accepts assessmentReport (createdTime/modifiedTime) and optional latestRun (completedAt).
 */
export function getDateOfAssessmentReport(assessmentReport, latestRun, withTime) {
  let format = 'DD MMM YYYY'
  if (withTime) format = 'DD MMM YYYY, h:mm A'
  const raw = latestRun?.completedAt ?? assessmentReport?.createdTime ?? assessmentReport?.modifiedTime
  if (raw == null) return null
  const d = parseReportTimestamp(raw)
  return d.isValid() ? d.local().format(format) : null
}

const reportHeadingStyles = {
  normal: { 
    fontWeight: '600', 
    fontSize: '14px',
    color: '#4338ca',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '8px',
    paddingBottom: '6px',
    borderBottom: '2px solid #e0e7ff',
  },
  larger: { 
    fontWeight: '700', 
    fontSize: '18px',
    color: '#312e81',
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    marginBottom: '12px',
    marginTop: '24px',
    paddingBottom: '8px',
    borderBottom: '3px solid #6366f1',
  },
}

export const TherapyReportHeader = ({
  therapyReportData,
  therapistName,
  templateData,
  therapyDate,
}) => {
  // if (!therapyReportData?.createdTime) {
  // }
  // Only show draft badge when status is explicitly 'draft'
  // Don't show for 'completed' or any other status
  // Strict check: only show if status is exactly 'draft'
  const isDraft = therapyReportData?.status === 'draft' || false

  return (
    <div style={{ background: '#FFF', position: 'relative' }}>
      <PMPHeadingBanner noColor={true} style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
          <span>{templateData?.headingText || getTherapyReportLabelFromEntity(therapyReportData)}</span>
          {isDraft && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 12px',
                backgroundColor: '#FEF3C7',
                border: '1.5px solid #F59E0B',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                color: '#92400E',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
                boxShadow: '0 1px 3px rgba(245, 158, 11, 0.3)',
              }}
            >
              <div
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: '#F59E0B',
                }}
              />
              Draft
            </span>
          )}
        </div>
      </PMPHeadingBanner>
      <PMPHeaderDetails noColor={true} style={{
        position: 'relative',
        zIndex: 1,
        padding: '16px 24px',
        backgroundColor: '#f8fafc',
        borderTop: '1px solid #e2e8f0',
        borderBottom: '1px solid #e2e8f0',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px 24px',
        alignItems: 'start',
      }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0 }}>
          <div style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
            Patient Name:{' '}
            <span style={{ textTransform: 'capitalize', color: '#1e293b', fontWeight: 600 }}>
              {therapyReportData?.patientName}{therapyReportData?.patientNumber ? ` (${therapyReportData.patientNumber})` : ''}
            </span>
          </div>
          <div style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
            Age/Gender:{' '}
            <span style={{ color: '#1e293b', fontWeight: 600 }}>
              {getAgeFromDobWithTime(therapyReportData?.patientDateOfBirth)}/
              {GENDER_LIST_ONBOARDING?.find((item) => item.value === therapyReportData?.patientGender)?.label ?? '—'}
            </span>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, textAlign: 'right' }}>
          <div style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
            Date & time:{' '}
            <span style={{ color: '#1e293b', fontWeight: 600 }}>
              {therapyDate
                ? dayjs.utc(therapyDate).local().format('DD MMM YYYY, h:mm A')
                : getDateOfTherapyReport(therapyReportData, true)}
            </span>
          </div>
          {therapistName && (
            <div style={{ fontSize: '14px', color: '#64748b', fontWeight: 500 }}>
              Therapist:{' '}
              <span style={{ color: '#1e293b', fontWeight: 600 }}>{therapistName}</span>
            </div>
          )}
        </div>
      </PMPHeaderDetails>
    </div>
  )
}

export const TherapyReportBodyFirstSection = ({
  therapyReportData,
  observationAndAdvice,
  selectedComplaints,
  selectedDiagnosis,
  selectedPrimaryClinicalDiagnosis,
  selectedDifferentialClinicalDiagnoses,
  selectedTherapyTypes,
  primaryTherapyType,
  additionalTherapyTypes,
  headingText,
}) => {
  // Discipline-aware default for the print heading. Caller can still override explicitly.
  const resolvedHeadingText = headingText ?? getTherapyReportLabelFromEntity(therapyReportData)
  // Use new structure if available, otherwise fall back to legacy
  const primaryTherapy = primaryTherapyType || (Array.isArray(selectedTherapyTypes) && selectedTherapyTypes.length > 0 ? selectedTherapyTypes[0] : null)
  const additionalTherapies = additionalTherapyTypes || (Array.isArray(selectedTherapyTypes) && selectedTherapyTypes.length > 1 ? selectedTherapyTypes.slice(1) : [])
  
  // Extract titles for clinical diagnosis
  // Primary Clinical Diagnosis is a single object, not an array
  const primaryClinicalTitle = selectedPrimaryClinicalDiagnosis && !Array.isArray(selectedPrimaryClinicalDiagnosis)
    ? (selectedPrimaryClinicalDiagnosis?.title || selectedPrimaryClinicalDiagnosis?.label || selectedPrimaryClinicalDiagnosis?.value || '')
    : ''
  
  const differentialClinicalTitles = selectedDifferentialClinicalDiagnoses && Array.isArray(selectedDifferentialClinicalDiagnoses) && selectedDifferentialClinicalDiagnoses.length > 0
    ? selectedDifferentialClinicalDiagnoses
        .map(item => {
          const title = item?.title || item?.label || item?.value || ''
          return title
        })
        .filter(Boolean)
    : []
  
  return (
    <>
      <div style={{ 
        textAlign: 'center', 
        marginBottom: '24px',
        paddingBottom: '20px',
        borderBottom: '2px solid #e2e8f0',
      }}>
        <div style={{
          fontSize: '24px',
          fontWeight: '700',
          color: '#1e293b',
          letterSpacing: '0.5px',
          marginBottom: '8px',
        }}>
          {resolvedHeadingText}
        </div>
        {therapyReportData?.mentorName && (
          <div style={{ 
            fontSize: '14px',
            color: '#64748b',
            fontWeight: '500',
            marginTop: '4px',
          }}>
            Consultant Psychologist: <span style={{ color: '#475569', fontWeight: '600' }}>{therapyReportData?.mentorName}</span>
          </div>
        )}
        {therapyReportData?.createdTime && (
          <div style={{ 
            fontSize: '13px',
            color: '#94a3b8',
            marginTop: '4px',
          }}>
            Prepared On: {dayjs.utc(therapyReportData?.createdTime).local().format('DD MMM YYYY, h:mm A')}
          </div>
        )}
      </div>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '24px', 
        paddingTop: '24px',
        paddingBottom: '16px',
      }}>
        {primaryTherapy && (
          <div style={{
            padding: '16px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}>
            <div style={reportHeadingStyles.normal}>Type of Therapy:</div>
            <div
              style={{
                fontWeight: '600',
                fontSize: '16px',
                color: '#1e40af',
                padding: '10px 14px',
                backgroundColor: '#dbeafe',
                borderRadius: '6px',
                borderLeft: '4px solid #3b82f6',
                marginTop: '8px',
              }}
            >
              {primaryTherapy.label || primaryTherapy.title}
            </div>
            {primaryTherapy.note && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#fff',
                borderRadius: '6px',
                border: '1px solid #e2e8f0',
              }}>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#64748b',
                  marginBottom: '6px',
                }}>
                  Notes:
                </div>
                <div style={{
                  fontSize: '14px',
                  color: '#334155',
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                }}>
                  {primaryTherapy.note}
                </div>
              </div>
            )}
          </div>
        )}
        {Array.isArray(additionalTherapies) && additionalTherapies.length > 0 && (
          <div style={{
            padding: '16px',
            backgroundColor: '#f8fafc',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}>
            <div style={reportHeadingStyles.normal}>Additional Therapy Types</div>
            <div style={{
              marginTop: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}>
              {additionalTherapies.map((therapyType, index) => (
                <div key={therapyType?.id || therapyType?.value || index} style={{
                  padding: '12px',
                  backgroundColor: '#fff',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                }}>
                  <div style={{
                    fontWeight: '600',
                    fontSize: '15px',
                    color: '#1e40af',
                    marginBottom: therapyType.note ? '8px' : '0',
                  }}>
                    {therapyType.label || therapyType.title}
                  </div>
                  {therapyType.note && (
                    <div style={{
                      marginTop: '8px',
                      paddingTop: '8px',
                      borderTop: '1px solid #e2e8f0',
                    }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#64748b',
                        marginBottom: '6px',
                      }}>
                        Notes:
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#334155',
                        lineHeight: '1.6',
                        whiteSpace: 'pre-wrap',
                      }}>
                        {therapyType.note}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        {observationAndAdvice?.observation && (
          <div style={{
            padding: '16px',
            backgroundColor: '#fefefe',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}>
            <div style={reportHeadingStyles.normal}>Observations</div>
            <div style={{ 
              whiteSpace: 'pre-wrap',
              marginTop: '8px',
              color: '#334155',
              fontSize: '15px',
              lineHeight: '1.7',
            }}>
              {observationAndAdvice?.observation}
            </div>
          </div>
        )}
        {Array.isArray(selectedComplaints) && selectedComplaints?.length > 0 && (
          <div style={{
            padding: '16px',
            backgroundColor: '#fefefe',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}>
            <div style={reportHeadingStyles.normal}>Presenting Concern</div>
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {selectedComplaints.map((complaint, index) => {
                const title = complaint?.title || complaint?.label || complaint?.value || ''
                const hasDetails = complaint?.note || complaint?.onsetTime || complaint?.durationSince
                
                if (!hasDetails) {
                  // Simple display if no extra details
                  return (
                    <div key={index} style={{ color: '#475569', fontSize: '15px' }}>
                      {title}
                    </div>
                  )
                }
                
                return (
                  <div 
                    key={complaint?.id || complaint?.value || index}
                    style={{
                      padding: '12px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '4px' }}>
                      {title}
                    </div>
                    {complaint?.note && (
                      <div style={{
                        fontSize: '14px',
                        color: '#334155',
                        marginTop: '8px',
                        whiteSpace: 'pre-wrap',
                      }}>
                        <strong>Note:</strong> {complaint.note}
                      </div>
                    )}
                    {(complaint?.onsetTime || complaint?.durationSince) && (
                      <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '16px',
                        marginTop: '8px',
                        fontSize: '14px',
                        color: '#334155',
                      }}>
                        {complaint?.onsetTime && (
                          <div>
                            <strong>Onset:</strong> {dayjs.utc(complaint.onsetTime).local().format('DD MMM YYYY')}
                          </div>
                        )}
                        {complaint?.durationSince && (
                          <div>
                            <strong>Duration:</strong> {complaint.durationSince} {complaint.durationType || 'days'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
        {Array.isArray(selectedDiagnosis) && selectedDiagnosis?.length > 0 && (
          <div style={{
            padding: '16px',
            backgroundColor: '#fefefe',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}>
            <div style={reportHeadingStyles.normal}>Diagnosis</div>
            <div style={{
              marginTop: '8px',
              color: '#475569',
              fontSize: '15px',
              lineHeight: '1.6',
            }}>
              {selectedDiagnosis.map((diagnosisItem) => diagnosisItem.label).join(', ')}
            </div>
          </div>
        )}
        {/* Primary Clinical Diagnosis - single object with note fields */}
        {primaryClinicalTitle && (
          <div style={{
            padding: '16px',
            backgroundColor: '#fefefe',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}>
            <div style={reportHeadingStyles.normal}>Primary Clinical Diagnosis</div>
            <div style={{
              marginTop: '8px',
              color: '#475569',
              fontSize: '15px',
              lineHeight: '1.6',
            }}>
              {primaryClinicalTitle}
            </div>
            {selectedPrimaryClinicalDiagnosis?.note && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#f8fafc',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#334155',
                lineHeight: '1.6',
                whiteSpace: 'pre-wrap',
              }}>
                <strong>Note:</strong> {selectedPrimaryClinicalDiagnosis.note}
              </div>
            )}
            {selectedPrimaryClinicalDiagnosis?.onsetTime && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#f8fafc',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#334155',
                lineHeight: '1.6',
              }}>
                <div style={{ marginBottom: selectedPrimaryClinicalDiagnosis.durationSince ? '8px' : '0' }}>
                  <strong>Onset Date:</strong> {dayjs.utc(selectedPrimaryClinicalDiagnosis.onsetTime).local().format('DD MMM YYYY')}
                </div>
                {selectedPrimaryClinicalDiagnosis.durationSince && (
                  <div>
                    <strong>Duration:</strong> {selectedPrimaryClinicalDiagnosis.durationSince} {selectedPrimaryClinicalDiagnosis.durationType || 'days'}
                  </div>
                )}
              </div>
            )}
            {selectedPrimaryClinicalDiagnosis?.durationSince && !selectedPrimaryClinicalDiagnosis?.onsetTime && (
              <div style={{
                marginTop: '12px',
                padding: '12px',
                backgroundColor: '#f8fafc',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#334155',
                lineHeight: '1.6',
              }}>
                <div>
                  <strong>Duration:</strong> {selectedPrimaryClinicalDiagnosis.durationSince} {selectedPrimaryClinicalDiagnosis.durationType || 'days'}
                </div>
              </div>
            )}
          </div>
        )}
        {/* Differential Clinical Diagnoses with note fields */}
        {differentialClinicalTitles.length > 0 && (
          <div style={{
            padding: '16px',
            backgroundColor: '#fefefe',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}>
            <div style={reportHeadingStyles.normal}>Differential Clinical Diagnoses</div>
            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {selectedDifferentialClinicalDiagnoses.map((diagnosis, index) => {
                const title = diagnosis?.title || diagnosis?.label || diagnosis?.value || ''
                if (!title) return null
                
                return (
                  <div 
                    key={index}
                    style={{
                      padding: '12px',
                      backgroundColor: '#f8fafc',
                      borderRadius: '6px',
                      border: '1px solid #e2e8f0',
                    }}
                  >
                    <div style={{ fontWeight: 600, color: '#1e293b', marginBottom: '4px' }}>
                      {title}
                    </div>
                    {diagnosis?.note && (
                      <div style={{
                        marginTop: '8px',
                        fontSize: '14px',
                        color: '#334155',
                        lineHeight: '1.6',
                        whiteSpace: 'pre-wrap',
                      }}>
                        <strong>Note:</strong> {diagnosis.note}
                      </div>
                    )}
                    {diagnosis?.onsetTime && (
                      <div style={{
                        marginTop: '8px',
                        padding: '12px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#334155',
                        lineHeight: '1.6',
                      }}>
                        <div style={{ marginBottom: diagnosis.durationSince ? '8px' : '0' }}>
                          <strong>Onset Date:</strong> {dayjs.utc(diagnosis.onsetTime).local().format('DD MMM YYYY')}
                        </div>
                        {diagnosis.durationSince && (
                          <div>
                            <strong>Duration:</strong> {diagnosis.durationSince} {diagnosis.durationType || 'days'}
                          </div>
                        )}
                      </div>
                    )}
                    {diagnosis?.durationSince && !diagnosis?.onsetTime && (
                      <div style={{
                        marginTop: '8px',
                        padding: '12px',
                        backgroundColor: '#f8fafc',
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#334155',
                        lineHeight: '1.6',
                      }}>
                        <div>
                          <strong>Duration:</strong> {diagnosis.durationSince} {diagnosis.durationType || 'days'}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export const TherapyReportBody = ({
  observationAndAdvice,
  content,
  nextVisitDate,
  nextVisitInputType,
  nextVisitInputValue,
  therapyDate, // This is the startTime of the current therapy session (bookedEventInfo.startTime)
  nextVisitBookedSlot,
  loadingNextVisitDetails,
  hasNextVisitBookedEventId,
  clinicTimezone, // Optional: clinic timezone (e.g., '+05:30' for IST)
}) => {
  const canShowNextVisitDate =
    nextVisitDate || 
    (nextVisitInputType && nextVisitInputValue && isValidNumber(nextVisitInputValue)) ||
    !!nextVisitBookedSlot ||
    loadingNextVisitDetails ||
    hasNextVisitBookedEventId
  
  // Debug logging
  
  return (
    <>
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '24px', 
        paddingTop: '24px',
        paddingBottom: '16px',
      }}>
        {observationAndAdvice?.advice && (
          <div style={{
            padding: '16px',
            backgroundColor: '#fefefe',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
          }}>
            <div style={reportHeadingStyles.normal}>Advice</div>
            <div style={{
              marginTop: '8px',
              color: '#334155',
              fontSize: '15px',
              lineHeight: '1.7',
              whiteSpace: 'pre-wrap',
            }}>
              {observationAndAdvice?.advice}
            </div>
          </div>
        )}
        {content && (
          <div style={{
            padding: '24px',
            backgroundColor: '#ffffff',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          }}>
            <div style={reportHeadingStyles.larger}>Notes</div>
            <div style={{
              marginTop: '16px',
            }}>
              <style>
                {`
                  .therapy-report-notes-content {
                    color: #334155;
                    font-size: 15px;
                    line-height: 1.8;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
                  }
                  .therapy-report-notes-content h1 {
                    font-size: 28px;
                    font-weight: 700;
                    color: #1e293b;
                    margin-top: 24px;
                    margin-bottom: 12px;
                    line-height: 1.3;
                  }
                  .therapy-report-notes-content h2 {
                    font-size: 22px;
                    font-weight: 700;
                    color: #1e293b;
                    margin-top: 20px;
                    margin-bottom: 10px;
                    line-height: 1.4;
                  }
                  .therapy-report-notes-content h3 {
                    font-size: 18px;
                    font-weight: 600;
                    color: #334155;
                    margin-top: 16px;
                    margin-bottom: 8px;
                    line-height: 1.5;
                  }
                  .therapy-report-notes-content p {
                    margin-bottom: 12px;
                    color: #475569;
                  }
                  .therapy-report-notes-content ul,
                  .therapy-report-notes-content ol {
                    margin: 12px 0;
                    padding-left: 24px;
                    color: #475569;
                  }
                  .therapy-report-notes-content li {
                    margin-bottom: 6px;
                    line-height: 1.7;
                  }
                  .therapy-report-notes-content strong {
                    color: #1e293b;
                    font-weight: 600;
                  }
                `}
              </style>
              <div className="therapy-report-notes-content">
                <PrescriptionWrapperForRichText>
                  {getParseDataFromHtmlString(content, { format: false })}
                </PrescriptionWrapperForRichText>
              </div>
            </div>
          </div>
        )}
        {canShowNextVisitDate && (
          <div 
            style={{
              padding: '20px',
              backgroundColor: nextVisitBookedSlot 
                ? (nextVisitBookedSlot?.status === 'booked' ? '#e8f5e8' : '#fff8e1')
                : '#f0fdf4',
              borderRadius: '8px',
              border: `1.5px solid ${
                nextVisitBookedSlot
                  ? (nextVisitBookedSlot?.status === 'booked' ? '#4caf50' : '#ffc107')
                  : '#bbf7d0'
              }`,
              borderLeft: `4px solid ${
                nextVisitBookedSlot
                  ? (nextVisitBookedSlot?.status === 'booked' ? '#4caf50' : '#ffc107')
                  : '#22c55e'
              }`,
              boxShadow: nextVisitBookedSlot ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
              minHeight: '60px', // Ensure minimum height for visibility
            }}
            data-testid="next-visit-card"
          >
            <div style={{
              ...reportHeadingStyles.normal,
              color: nextVisitBookedSlot
                ? (nextVisitBookedSlot?.status === 'booked' ? '#2e7d32' : '#ff8f00')
                : '#166534',
              borderBottom: `2px solid ${
                nextVisitBookedSlot
                  ? (nextVisitBookedSlot?.status === 'booked' ? '#81c784' : '#ffb74d')
                  : '#86efac'
              }`,
            }}>
              {nextVisitBookedSlot 
                ? `Next Visit Slot ${nextVisitBookedSlot?.status === 'booked' ? 'Booked' : 'Blocked'}`
                : 'Next Visit Date'
              }
            </div>
            
            {loadingNextVisitDetails ? (
              <div style={{
                marginTop: '12px',
                color: '#666',
                fontSize: '14px',
              }}>
                Loading next visit details...
              </div>
            ) : nextVisitBookedSlot ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
                
                {/* Time Slot */}
                {(() => {
                  // Get timezone - use clinic timezone if available, fallback to IST (+05:30)
                  let timezone = clinicTimezone || nextVisitBookedSlot?.timezone || '+05:30'
                  
                  // Decode if it's URL encoded
                  if (timezone && typeof timezone === 'string' && timezone.includes('%')) {
                    try {
                      timezone = decodeURIComponent(timezone)
                    } catch {
                      timezone = '+05:30'
                    }
                  }
                  
                  const timeSlot = nextVisitBookedSlot.timeSlot || (() => {
                    if (nextVisitBookedSlot.startTime) {
                      try {
                        // Parse times - they come in UTC, convert to clinic timezone using moment
                        const start = dayjs.utc(nextVisitBookedSlot.startTime)
                        const end = nextVisitBookedSlot.endTime 
                          ? dayjs.utc(nextVisitBookedSlot.endTime) 
                          : dayjs.utc(nextVisitBookedSlot.startTime).add(30, 'minutes')
                        
                        // Convert to clinic timezone
                        const startInTz = start.utcOffset(timezone)
                        const endInTz = end.utcOffset(timezone)
                        
                        // Format times
                        const startFormatted = startInTz.format('h:mm A')
                        const endFormatted = endInTz.format('h:mm A')
                        
                        // Get timezone abbreviation (e.g., IST, EST)
                        let tzAbbr = 'IST' // Default to IST
                        try {
                          // Map common timezone offsets to abbreviations
                          if (timezone === '+05:30' || timezone === 'Asia/Kolkata' || timezone === 'Asia/Calcutta') {
                            tzAbbr = 'IST'
                          } else if (timezone === '+05:00') {
                            tzAbbr = 'PKT'
                          } else if (timezone === '+08:00') {
                            tzAbbr = 'CST'
                          } else if (timezone === '+00:00' || timezone === 'UTC') {
                            tzAbbr = 'UTC'
                          } else {
                            // Try to extract offset and format as GMT+X
                            const offsetMatch = timezone.match(/^([+-])(\d{2}):(\d{2})$/)
                            if (offsetMatch) {
                              const sign = offsetMatch[1]
                              const hours = parseInt(offsetMatch[2], 10)
                              const minutes = parseInt(offsetMatch[3], 10)
                              if (minutes === 0) {
                                tzAbbr = `GMT${sign}${hours}`
                              } else {
                                tzAbbr = `GMT${sign}${hours}:${minutes}`
                              }
                            } else {
                              tzAbbr = timezone
                            }
                          }
                        } catch {
                          tzAbbr = 'IST'
                        }
                        
                        return `${startFormatted} - ${endFormatted} ${tzAbbr}`
                      } catch (e) {
                        console.error('Error formatting time slot:', e)
                        return null
                      }
                    }
                    return null
                  })()
                  
                  return timeSlot ? (
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '4px' }}>
                      {timeSlot}
                    </div>
                  ) : null
                })()}
                
                {/* Date */}
                {(() => {
                  const visitDate = nextVisitBookedSlot.startTime 
                    ? dayjs(nextVisitBookedSlot.startTime)
                    : (nextVisitDate ? dayjs(nextVisitDate) : null)
                  
                  if (!visitDate) {
                    return (
                      <div style={{ fontSize: '14px', color: '#64748b', fontStyle: 'italic', marginBottom: '8px' }}>
                        Next visit slot is booked/blocked
                      </div>
                    )
                  }
                  
                  const dateDisplay = nextVisitBookedSlot.startTime 
                    ? visitDate.format('dddd, MMMM D, YYYY')
                    : visitDate.format('DD MMM YYYY')
                  
                  // Calculate relative time from therapyDate (session start time), not current time
                  const referenceDate = therapyDate ? dayjs(therapyDate) : dayjs()
                  const relativeTime = getExactDiffFromCurrentTimeDayJs(visitDate, referenceDate, true)
                  
                  return (
                    <div style={{ marginBottom: '8px' }}>
                      <div style={{ fontSize: '15px', color: '#666', fontWeight: '600' }}>
                        {dateDisplay}
                      </div>
                      <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px', fontStyle: 'italic' }}>
                        {relativeTime}
                      </div>
                    </div>
                  )
                })()}

                {/* Details Section */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                }}>
                  {/* Event Type */}
                  {nextVisitBookedSlot?.eventType && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                      <CalendarMonthIcon style={{ fontSize: '18px', color: '#666' }} />
                      <span style={{ color: '#666' }}>Event Type:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {displayNamesOfEventTypes?.[nextVisitBookedSlot.eventType] || nextVisitBookedSlot.eventType}
                      </span>
                    </div>
                  )}

                  {/* Meeting Mode (Online/Offline) */}
                  {nextVisitBookedSlot?.meetingSrc && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                      {nextVisitBookedSlot.meetingSrc === 'aumhum' || nextVisitBookedSlot.meetingSrc === 'online' ? (
                        <VideocamIcon style={{ fontSize: '18px', color: '#1976d2' }} />
                      ) : (
                        <LocationOnIcon style={{ fontSize: '18px', color: '#d32f2f' }} />
                      )}
                      <span style={{ color: '#666' }}>Mode:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {BOOKED_EVENT_MODES?.[nextVisitBookedSlot.meetingSrc]?.title || 
                         (nextVisitBookedSlot.meetingSrc === 'aumhum' || nextVisitBookedSlot.meetingSrc === 'online' ? 'Online' : 'Offline')}
                      </span>
                    </div>
                  )}

                  {/* Chamber Room (for offline sessions) */}
                  {nextVisitBookedSlot?.chamberRoomName && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                      <LocationOnIcon style={{ fontSize: '18px', color: '#d32f2f' }} />
                      <span style={{ color: '#666' }}>Location:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {nextVisitBookedSlot.chamberRoomName}
                      </span>
                    </div>
                  )}

                  {/* Duration */}
                  {nextVisitBookedSlot?.startTime && nextVisitBookedSlot?.endTime && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px' }}>
                      <AccessTime style={{ fontSize: '18px', color: '#666' }} />
                      <span style={{ color: '#666' }}>Duration:</span>
                      <span style={{ fontWeight: '600', color: '#333' }}>
                        {(() => {
                          try {
                            const start = dayjs(nextVisitBookedSlot.startTime)
                            const end = dayjs(nextVisitBookedSlot.endTime)
                            const durationMinutes = end.diff(start, 'minute')
                            if (durationMinutes < 60) {
                              return `${durationMinutes} minutes`
                            }
                            const hours = Math.floor(durationMinutes / 60)
                            const minutes = durationMinutes % 60
                            return minutes > 0 ? `${hours}h ${minutes}m` : `${hours} hour${hours > 1 ? 's' : ''}`
                          } catch {
                            return 'N/A'
                          }
                        })()}
                      </span>
                    </div>
                  )}

                  {/* Fee Information removed from report preview */}
                  
                  {/* Fallback: If no details are shown, at least show status */}
                  {!nextVisitBookedSlot?.eventType && 
                   !nextVisitBookedSlot?.meetingSrc && 
                   !nextVisitBookedSlot?.chamberRoomName && 
                   !nextVisitBookedSlot?.startTime && 
                   !nextVisitBookedSlot?.feePaid && (
                    <div style={{ fontSize: '14px', color: '#64748b', fontStyle: 'italic', paddingTop: '8px' }}>
                      Next visit slot details are available but incomplete.
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{
                marginTop: '8px',
              }}>
                {(() => {
                  let visitDate = null
                  let dateDisplay = null
                  
                  if (nextVisitDate) {
                    visitDate = dayjs(nextVisitDate)
                    dateDisplay = visitDate.format('DD MMM YYYY')
                  } else if (nextVisitInputValue && nextVisitInputType) {
                    const calculatedDate = calculateNextVisitDate(
                      nextVisitInputValue,
                      nextVisitInputType,
                      therapyDate,
                      'D MMM YYYY',
                    )
                    // Calculate the actual date for relative time
                    const currentDate = therapyDate ? dayjs(therapyDate) : dayjs()
                    visitDate = currentDate.add(nextVisitInputValue, nextVisitInputType)
                    dateDisplay = calculatedDate
                  }
                  
                  if (dateDisplay) {
                    // Calculate relative time from therapyDate (session start time), not current time
                    const referenceDate = therapyDate ? dayjs(therapyDate) : dayjs()
                    const relativeTime = visitDate ? getExactDiffFromCurrentTimeDayJs(visitDate, referenceDate, true) : null
                    return (
                      <>
                        <div style={{ color: '#166534', fontSize: '16px', fontWeight: '600' }}>
                          {dateDisplay}
                        </div>
                        {relativeTime && (
                          <div style={{ fontSize: '13px', color: '#94a3b8', marginTop: '4px', fontStyle: 'italic' }}>
                            {relativeTime}
                          </div>
                        )}
                      </>
                    )
                  }
                  
                  if (hasNextVisitBookedEventId) {
                    return (
                      <span style={{ color: '#64748b', fontSize: '14px', fontStyle: 'italic' }}>
                        Next visit slot details are being loaded...
                      </span>
                    )
                  }
                  
                  return (
                    <span style={{ color: '#64748b', fontSize: '14px' }}>
                      No next visit date set
                    </span>
                  )
                })()}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

export const TherapyReportFooter = ({ therapyReportData, showPageNumbers = true }) => {
  const printedOn = dayjs().format('DD/MM/YYYY HH:mm')
  const printedBy = therapyReportData?.mentorName || 'System'
  
  return (
    <>
      <PMPFooterContainer noColor={true}>
        <div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', width: '40%' }}
        >
          <div style={{ textAlign: 'right' }}>{therapyReportData?.footer?.headline}</div>
          <div style={{ textAlign: 'right' }}>{therapyReportData?.footer?.subtext}</div>
          <div style={{ textAlign: 'right' }}>{therapyReportData?.footer?.supportText}</div>
        </div>
        <div
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', width: '40%' }}
        >
          <div>{therapyReportData?.footer?.headlineAlt}</div>
          <div>{therapyReportData?.footer?.subtextAlt}</div>
          <div>{therapyReportData?.footer?.supportTextAlt}</div>
        </div>
      </PMPFooterContainer>
      {/* Print footer with printed on/by and page numbers - visible in print only */}
      {showPageNumbers === true ? (
        <div className="therapy-report-print-footer-info" style={{ display: 'none' }}>
          <span className="printed-on">Printed on: {printedOn}</span>
          <span className="printed-by">Printed by: {printedBy}</span>
        </div>
      ) : null}
      {/* Page number footer - visible in print only when showPageNumbers is true */}
      {showPageNumbers === true ? (
        <div className="therapy-report-page-footer">
          Page <span className="page-number"></span>
        </div>
      ) : null}
    </>
  )
}

/////////////////////////////////////////////////////////////////////// NEW prescription drug selection related components:

// Modern styles for select menus
export const commonStyles = {
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999, // Ensure portal renders above all other components
  }),
  menuList: (provided, state) => ({
    ...provided,
    maxHeight: '240px',
    overflowY: 'auto',
    padding: '4px',
    backgroundColor: '#ffffff', // Pure white menu background
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(139, 92, 246, 0.3) rgba(139, 92, 246, 0.1)',
    '&::-webkit-scrollbar': {
      width: '8px',
    },
    '&::-webkit-scrollbar-track': {
      background: 'rgba(139, 92, 246, 0.05)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(139, 92, 246, 0.3)',
      borderRadius: '4px',
      '&:hover': {
        background: 'rgba(139, 92, 246, 0.5)',
      },
    },
  }),
  menu: (provided, state) => ({
    ...provided,
    borderRadius: '10px',
    border: '1px solid rgba(139, 92, 246, 0.25)',
    backgroundColor: '#ffffff', // Pure white menu background
    boxShadow: '0 4px 16px rgba(139, 92, 246, 0.15), 0 2px 8px rgba(139, 92, 246, 0.1)',
    marginTop: '4px',
    zIndex: 9999, // Ensure dropdown renders above other components
    overflow: 'hidden',
  }),
  control: (provided, state) => ({
    ...provided,
    minHeight: '44px',
    borderRadius: '10px',
    border: state.isFocused 
      ? '1px solid rgba(139, 92, 246, 0.6)' 
      : '1px solid rgba(139, 92, 246, 0.25)',
    backgroundColor: '#ffffff', // Pure white to stand out from section background
    boxShadow: state.isFocused 
      ? '0 0 0 3px rgba(139, 92, 246, 0.15)' 
      : '0 2px 4px rgba(139, 92, 246, 0.1)',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: 'rgba(139, 92, 246, 0.45)',
      boxShadow: '0 2px 8px rgba(139, 92, 246, 0.15)',
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '8px 12px',
  }),
  input: (provided) => ({
    ...provided,
    margin: 0,
    padding: 0,
    color: '#6b21a8', // Purple text color
    fontSize: '14px',
  }),
  placeholder: (provided) => ({
    ...provided,
    color: 'rgba(139, 92, 246, 0.5)',
    fontSize: '14px',
  }),
  singleValue: (provided) => ({
    ...provided,
    color: '#6b21a8', // Purple text color
    fontSize: '14px',
    fontWeight: 500,
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: 'rgba(139, 92, 246, 0.12)',
    borderRadius: '6px',
    border: '1px solid rgba(139, 92, 246, 0.25)',
    padding: '2px 6px',
    margin: '2px',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: '#7c3aed', // Purple text color
    fontSize: '13px',
    fontWeight: 500,
    padding: '2px 4px',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: '#7c3aed',
    '&:hover': {
      backgroundColor: 'rgba(139, 92, 246, 0.2)',
      color: '#6b21a8',
    },
  }),
  indicatorSeparator: (provided) => ({
    ...provided,
    backgroundColor: 'rgba(139, 92, 246, 0.25)',
  }),
  dropdownIndicator: (provided) => ({
    ...provided,
    color: '#7c3aed',
    padding: '6px',
    '&:hover': {
      color: '#6b21a8',
    },
  }),
  clearIndicator: (provided) => ({
    ...provided,
    color: '#9ca3af',
    padding: '6px',
    '&:hover': {
      color: '#ef4444',
    },
  }),
  option: (provided, state) => ({
    ...provided,
    borderRadius: '6px',
    padding: '10px 12px',
    fontSize: '14px',
    color: state.isSelected ? '#6b21a8' : '#6b21a8',
    backgroundColor: state.isSelected
      ? 'rgba(139, 92, 246, 0.15)'
      : state.isFocused
      ? 'rgba(139, 92, 246, 0.1)'
      : 'transparent',
    fontWeight: state.isSelected ? 600 : 500,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    margin: '2px 0',
    '&:hover': {
      backgroundColor: 'rgba(139, 92, 246, 0.12)',
      color: '#6b21a8',
    },
    '&:active': {
      backgroundColor: 'rgba(139, 92, 246, 0.2)',
    },
  }),
}

////////////////////////////////////////////////////////////////////////// NEW prescription components

// Modern styled textarea component
const ModernTextAreaBox = styled(PMPSectionTextAreaBox)({
  padding: '12px 16px',
  borderRadius: '10px',
  border: '1px solid rgba(139, 92, 246, 0.25)',
  backgroundColor: '#ffffff', // Pure white to stand out from section background
  fontSize: '14px',
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  color: '#6b21a8', // Purple text color
  lineHeight: '1.6',
  minHeight: '100px',
  boxShadow: '0 2px 4px rgba(139, 92, 246, 0.1)',
  transition: 'all 0.2s ease',
  resize: 'vertical',
  '&:focus': {
    outline: 'none',
    borderColor: 'rgba(139, 92, 246, 0.6)',
    boxShadow: '0 0 0 3px rgba(139, 92, 246, 0.15)',
  },
  '&::placeholder': {
    color: 'rgba(139, 92, 246, 0.5)',
  },
})

export const PrescriptionTextAreaComponent = ({ inputValue, setInputValue, placeholder }) => {
  // Convert null/undefined to empty string to prevent React warnings
  const safeValue = inputValue ?? ''
  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
      }}
    >
      <ModernTextAreaBox
        value={safeValue}
        onChange={(e) => setInputValue(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  )
}

// Note: ModernSuggestedTherapySelectMenu is now imported directly in index.jsx
// to avoid circular dependencies. The component uses commonStyles which is already modernized.

const loadOptionsComplaints = async (search, loadedOptions, additional) => {
  // Extract page and clinicId from additional, defaulting page to 1 if not provided
  const page = additional?.page || 1
  const clinicId = additional?.clinicId
  
  const response = await getCPDataWithoutParams(
    `/api/secure/clinic-practitioner/health-complaint/list?search=${search || ''}&clinicId=${clinicId}&page=${page}&pageSize=5`,
  )
  var hasMore = true
  var optionsArray = []

  if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
    optionsArray = response?.data?.map((option, index) => {
      return {
        ...option,
        value: option?.title,
        label: option?.title,
      }
    })
    
    // If we got fewer results than pageSize, there are no more pages
    if (response.data.length < 5) {
      hasMore = false
    }
  } else {
    hasMore = false
  }

  return {
    options: optionsArray,
    hasMore,
    additional: {
      page: page + 1,
      clinicId,
    },
  }
}

const loadOptionsDiagnosis = async (search, loadedOptions, additional) => {
  // Extract page and clinicId from additional, defaulting page to 1 if not provided
  const page = additional?.page || 1
  const clinicId = additional?.clinicId
  
  const response = await getCPDataWithoutParams(
    `/api/secure/clinic-practitioner/diagnosis/list?search=${search || ''}&clinicId=${clinicId}&page=${page}&pageSize=5`,
  )
  var hasMore = true
  var optionsArray = []

  if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
    optionsArray = response?.data?.map((option, index) => {
      return {
        ...option,
        value: option?.title,
        label: option?.title,
      }
    })
    
    // If we got fewer results than pageSize, there are no more pages
    if (response.data.length < 5) {
      hasMore = false
    }
  } else {
    hasMore = false
  }

  return {
    options: optionsArray,
    hasMore,
    additional: {
      page: page + 1,
      clinicId,
    },
  }
}

const loadOptionsClinicalDiagnosis = async (search, loadedOptions, additional) => {
  // Extract page and clinicId from additional, defaulting page to 1 if not provided
  const page = additional?.page || 1
  const clinicId = additional?.clinicId
  
  // Clinical diagnosis API uses entityType as a query parameter
  const response = await getCPDataWithoutParams(
    `/api/secure/clinic-practitioner/clinical-diagnosis/list?search=${search || ''}&clinicId=${clinicId}&page=${page}&pageSize=5`,
  )
  var hasMore = true
  var optionsArray = []

  if (response.status === 200 && Array.isArray(response.data) && response.data.length > 0) {
    optionsArray = response?.data?.map((option, index) => {
      return {
        ...option,
        value: option?.id || option?.title,
        label: option?.title,
      }
    })
    
    // If we got fewer results than pageSize, there are no more pages
    if (response.data.length < 5) {
      hasMore = false
    }
  } else {
    hasMore = false
  }

  return {
    options: optionsArray,
    hasMore,
    additional: {
      page: page + 1,
      clinicId,
    },
  }
}

export const ComplaintsSelectMenu = ({ clinicId, selectedComplaints, setSelectedComplaints }) => {
  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <CreatableAsyncPaginate
        id="complaintsSelection"
        placeholder=""
        isMulti={true}
        isClearable={true}
        isSearchable={true}
        defaultOptions={false}
        loadOptions={loadOptionsComplaints}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        value={selectedComplaints}
        onChange={(value) => setSelectedComplaints(value)}
        debounceTimeout={500}
        additional={{ page: 1, clinicId: clinicId }}
        styles={commonStyles}
        shouldLoadMore={(scrollHeight, clientHeight, scrollTop) => {
          // Load more when user scrolls within 50px of the bottom
          const threshold = 50
          return scrollHeight - clientHeight - scrollTop < threshold
        }}
        reduceOptions={(prevOptions, loadedOptions) => {
          // Merge new options with existing ones, preventing duplicates based on value
          const existingValues = new Set(prevOptions.map((opt) => opt?.value))
          const newOptions = loadedOptions.filter((opt) => !existingValues.has(opt?.value))
          return [...prevOptions, ...newOptions]
        }}
      />
    </div>
  )
}
const CreatableAsyncPaginate = withAsyncPaginate(Creatable)

export const DiagnosisSelectMenu = ({ clinicId, selectedDiagnosis, setSelectedDiagnosis }) => {
  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <CreatableAsyncPaginate
        id="diagnosisSelection"
        placeholder=""
        isMulti={true}
        isClearable={true}
        isSearchable={true}
        defaultOptions={false}
        loadOptions={loadOptionsDiagnosis}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        value={selectedDiagnosis}
        onChange={(value) => {
          setSelectedDiagnosis(value)
        }}
        debounceTimeout={500}
        additional={{ page: 1, clinicId: clinicId }}
        styles={commonStyles}
        shouldLoadMore={(scrollHeight, clientHeight, scrollTop) => {
          // Load more when user scrolls within 50px of the bottom
          const threshold = 50
          return scrollHeight - clientHeight - scrollTop < threshold
        }}
        reduceOptions={(prevOptions, loadedOptions) => {
          // Merge new options with existing ones, preventing duplicates based on value
          const existingValues = new Set(prevOptions.map((opt) => opt?.value))
          const newOptions = loadedOptions.filter((opt) => !existingValues.has(opt?.value))
          return [...prevOptions, ...newOptions]
        }}
      />
    </div>
  )
}

export const ClinicalDiagnosisSelectMenu = ({ clinicId, selectedClinicalDiagnosis, setSelectedClinicalDiagnosis, isMulti = true }) => {
  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <CreatableAsyncPaginate
        id="clinicalDiagnosisSelection"
        placeholder=""
        isMulti={isMulti}
        isClearable={true}
        isSearchable={true}
        defaultOptions={false}
        loadOptions={loadOptionsClinicalDiagnosis}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        value={selectedClinicalDiagnosis}
        onChange={(value) => {
          // Ensure onChange is called even with null/undefined
          setSelectedClinicalDiagnosis(value || null)
        }}
        debounceTimeout={500}
        additional={{ page: 1, clinicId: clinicId }}
        styles={commonStyles}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        shouldLoadMore={(scrollHeight, clientHeight, scrollTop) => {
          // Load more when user scrolls within 50px of the bottom
          const threshold = 50
          return scrollHeight - clientHeight - scrollTop < threshold
        }}
        reduceOptions={(prevOptions, loadedOptions) => {
          // Merge new options with existing ones, preventing duplicates based on value
          const existingValues = new Set(prevOptions.map((opt) => opt?.value))
          const newOptions = loadedOptions.filter((opt) => !existingValues.has(opt?.value))
          return [...prevOptions, ...newOptions]
        }}
      />
    </div>
  )
}

export const PrescriptionNextVisitComponent = ({
  nextVisitInputType,
  setNextVisitInputType,
  nextVisitInputValue,
  setNextVisitInputValue,
}) => {
  let formattedNextDate

  return (
    <div style={{ display: 'flex', gap: '20px' }}>
      <PMPSectionInputLabel>Next Visit</PMPSectionInputLabel>
      <div style={{ width: '90px' }}>
        <PMPSectionInputBox
          type="number"
          placeholder="Enter number"
          style={{ 
            textAlign: 'center',
            fontWeight: 500,
            fontSize: '15px',
          }}
          value={nextVisitInputValue === null || nextVisitInputValue === undefined || nextVisitInputValue === 0 ? '' : nextVisitInputValue}
          onChange={(e) => {
            const inputValue = e.target.value
            
            // Allow empty string for clearing the field
            if (inputValue === '' || inputValue === null) {
              setNextVisitInputValue('')
              return
            }
            
            // Handle negative sign or invalid characters early
            if (inputValue === '-' || inputValue === '.' || inputValue === '+' || inputValue === 'e' || inputValue === 'E') {
              return
            }
            
            // Get valid number
            const numValue = Number(inputValue)
            
            // Only update if it's a valid positive integer or empty
            if (!isNaN(numValue) && Number.isInteger(numValue) && numValue > 0) {
              // Set reasonable max limits based on input type
              const maxValue = nextVisitInputType === 'days' ? 365 : nextVisitInputType === 'weeks' ? 52 : 12
              if (numValue <= maxValue) {
                setNextVisitInputValue(numValue)
              }
            } else if (inputValue === '') {
              setNextVisitInputValue('')
            }
          }}
          onKeyDown={(e) => {
            // Prevent 'e', 'E', '+', '-' keys
            if (e.key === 'e' || e.key === 'E' || e.key === '+' || e.key === '-') {
              e.preventDefault()
            }
          }}
          min="1"
          max={nextVisitInputType === 'days' ? 365 : nextVisitInputType === 'weeks' ? 52 : 12}
          step="1"
        />
      </div>
      <div style={{ width: '200px' }}>
        <RectangleRadioButton
          disabled={false}
          options={[
            { label: 'Days', id: 'days' },
            { label: 'Weeks', id: 'weeks' },
            { label: 'Months', id: 'months' },
          ]}
          selectedId={nextVisitInputType}
          selectionFunction={(id) => setNextVisitInputType(id)}
        />
      </div>
      {nextVisitInputValue && nextVisitInputType && (
        <div>({calculateNextVisitDate(nextVisitInputValue, nextVisitInputType)})</div>
      )}
    </div>
  )
}

///// helper functions:

// complaints array related:

export function getComplaintsArrayForSubmission(complaintsArray, clinicId) {
  const toReturn = []
  if (Array.isArray(complaintsArray) && complaintsArray.length > 0) {
    complaintsArray?.forEach((complaintItem) => {
      if (complaintItem?.label || complaintItem?.title || complaintItem?.id) {
        // Preserve all fields from the complaint object
        const fullComplaint = {
          ...complaintItem,
          // Ensure required fields are present
          apiParam: complaintItem?.apiParam || 'health-complaint',
          title: complaintItem?.title || complaintItem?.label || complaintItem?.value,
        }
        // Add clinicId if provided
        if (clinicId) {
          fullComplaint.clinicId = clinicId
        }
        toReturn.push(fullComplaint)
      }
    })
  }
  return toReturn
}

export const getInitialDataForSettingExistingSelectedComplaints = (complaintsFromServer) => {
  const toReturn = []
  if (Array.isArray(complaintsFromServer) && complaintsFromServer.length > 0) {
    complaintsFromServer?.forEach((item) => {
      toReturn.push({ label: item?.title, value: item?.title })
    })
  }
  return toReturn
}

// diagnosis array related:

export function getDiagnosisArrayForSubmission(diagnosisArray, clinicId) {
  const toReturn = []
  if (Array.isArray(diagnosisArray) && diagnosisArray.length > 0) {
    diagnosisArray?.forEach((diagnosisItem) => {
      if (diagnosisItem?.label || diagnosisItem?.title || diagnosisItem?.id) {
        // Preserve all fields from the diagnosis object
        const fullDiagnosis = {
          ...diagnosisItem,
          // Ensure required fields are present
          apiParam: diagnosisItem?.apiParam || 'diagnosis',
          title: diagnosisItem?.title || diagnosisItem?.label || diagnosisItem?.value,
        }
        // Add clinicId if provided
        if (clinicId) {
          fullDiagnosis.clinicId = clinicId
        }
        toReturn.push(fullDiagnosis)
      }
    })
  }
  return toReturn
}

export const getInitialDataForSettingExistingSelectedDiagnosis = (diagnosisFromServer) => {
  const toReturn = []
  if (Array.isArray(diagnosisFromServer) && diagnosisFromServer.length > 0) {
    diagnosisFromServer?.forEach((item) => {
      toReturn.push({ label: item?.title, value: item?.title })
    })
  }
  return toReturn
}

// clinical diagnosis array related:

// Helper to convert single ClinicalDiagnosis object to submission format
// Includes all ClinicalDiagnosisNote fields from docs/clinical-diagnosis-note.md
export function getClinicalDiagnosisForSubmission(clinicalDiagnosis, clinicId = null) {
  // Check if clinicalDiagnosis is null, undefined, or truly empty (no meaningful data)
  if (!clinicalDiagnosis) {
    return null
  }
  
  // Check if it's an empty object (no properties)
  if (typeof clinicalDiagnosis === 'object' && Object.keys(clinicalDiagnosis).length === 0) {
    return null
  }
  
  // Preserve all fields from the original object
  const fullDiagnosis = { ...clinicalDiagnosis }
  
  // Ensure required fields are present - check multiple possible field names
  if (!fullDiagnosis.id) {
    fullDiagnosis.id = clinicalDiagnosis?.id || clinicalDiagnosis?.value || clinicalDiagnosis?._id
  }
  if (!fullDiagnosis.title) {
    fullDiagnosis.title = clinicalDiagnosis?.title || clinicalDiagnosis?.label || clinicalDiagnosis?.value
  }
  
  // If we still don't have id or title, the object might be invalid
  if (!fullDiagnosis.id && !fullDiagnosis.title) {
    return null
  }
  
  // Update apiParam: if it exists and doesn't already end with "__NOTE", append "__NOTE", otherwise use 'clinical-diagnosis__NOTE'
  if (fullDiagnosis.apiParam) {
    // Check if apiParam already ends with '__NOTE' to avoid double appending
    if (!fullDiagnosis.apiParam.endsWith('__NOTE')) {
      fullDiagnosis.apiParam = fullDiagnosis.apiParam + '__NOTE'
    }
  } else {
    fullDiagnosis.apiParam = 'clinical-diagnosis__NOTE'
  }
  
  // Add clinicId if provided
  if (clinicId) {
    fullDiagnosis.clinicId = clinicId
  }
  
  return fullDiagnosis
}

export function getClinicalDiagnosisArrayForSubmission(clinicalDiagnosisArray, clinicId = null) {
  const toReturn = []
  if (Array.isArray(clinicalDiagnosisArray) && clinicalDiagnosisArray.length > 0) {
    clinicalDiagnosisArray?.forEach((diagnosisItem) => {
      // Process any non-null item - getClinicalDiagnosisForSubmission will handle field mapping
      if (diagnosisItem != null && typeof diagnosisItem === 'object') {
        // Use the same logic as getClinicalDiagnosisForSubmission to return full objects
        const fullDiagnosisObj = getClinicalDiagnosisForSubmission(diagnosisItem, clinicId)
        if (fullDiagnosisObj) {
          toReturn.push(fullDiagnosisObj)
        }
      }
    })
  }
  return toReturn
}

// Helper to convert single ClinicalDiagnosis from server to form format
export const getInitialDataForSettingExistingSelectedPrimaryClinicalDiagnosis = (clinicalDiagnosisFromServer) => {
  // Handle single object (primary) or array with first item
  if (!clinicalDiagnosisFromServer) return null
  
  const item = Array.isArray(clinicalDiagnosisFromServer) 
    ? (clinicalDiagnosisFromServer.length > 0 ? clinicalDiagnosisFromServer[0] : null)
    : clinicalDiagnosisFromServer
    
  if (!item) return null
  
  return {
    ...item,
    label: item?.title || item?.label,
    value: item?.id || item?.value || item?.title
  }
}

export const getInitialDataForSettingExistingSelectedClinicalDiagnosis = (clinicalDiagnosisFromServer) => {
  const toReturn = []
  if (Array.isArray(clinicalDiagnosisFromServer) && clinicalDiagnosisFromServer.length > 0) {
    clinicalDiagnosisFromServer?.forEach((item) => {
      toReturn.push({ 
        ...item,
        label: item?.title || item?.label, 
        value: item?.id || item?.value || item?.title 
      })
    })
  }
  return toReturn
}

// therapy type array related (for new structure):

export function getTherapyTypeForSubmission(therapyTypeObject) {
  // Primary therapy type - single object (not array)
  if (therapyTypeObject) {
    // The therapy object from SuggestedTherapySelectMenu includes the full object with id, title, etc.
    // We need to preserve all fields including apiParam
    if (therapyTypeObject?.id) {
      // Return the full object with all fields, ensuring apiParam is set
      return {
        ...therapyTypeObject,
        id: therapyTypeObject.id,
        title: therapyTypeObject.title || therapyTypeObject.label,
        apiParam: therapyTypeObject.apiParam || 'therapy-type',
      }
    } else if (therapyTypeObject?.value && typeof therapyTypeObject.value === 'string') {
      // Legacy case: if it's just a string value (title), we need to use it as both id and title
      // This happens when the therapy was manually created or from legacy data
      return {
        ...therapyTypeObject,
        id: therapyTypeObject.value,
        title: therapyTypeObject.label || therapyTypeObject.value,
        apiParam: therapyTypeObject.apiParam || 'therapy-type',
      }
    }
  }
  return null
}

// Helper to convert therapy types from server format to select menu format
export const getInitialDataForSettingExistingSelectedTherapiesForTherapyReport = (therapiesFromServer) => {
  const toReturn = []
  if (Array.isArray(therapiesFromServer) && therapiesFromServer.length > 0) {
    therapiesFromServer?.forEach((item) => {
      // Handle both formats: string (legacy) or object (new format with id/title)
      if (typeof item === 'string') {
        toReturn.push({ label: item, value: item, note: '' })
      } else if (item?.id || item?.title) {
        // New format: object with id and title
        // Preserve note field if it exists
        toReturn.push({
          ...item,
          label: item?.title || item?.label || item?.value || '',
          value: item?.id || item?.value || item?.title || '',
          note: item?.note || '',
        })
      } else if (item?.label || item?.value) {
        // Already in select menu format
        // Preserve note field if it exists
        toReturn.push({
          ...item,
          note: item?.note || '',
        })
      }
    })
  }
  return toReturn
}

// Re-export utility functions for backward compatibility
export { calculateNextVisitDate } from './utils/therapyReportUtils'

export const getNextVisitDateForSubmission = (
  nextVisitInputValue,
  nextVisitInputType,
  bookedEventInfoStartTime = dayjs(),
) => {
  if (!nextVisitInputValue || !nextVisitInputType) {
    return null
  }
  
  const calculatedDate = calculateNextVistDateInMoment(
    nextVisitInputValue,
    nextVisitInputType,
    bookedEventInfoStartTime,
  )
  
  // Validate the calculated date before formatting
  if (!calculatedDate || !calculatedDate.isValid()) {
    return null
  }
  
  // Convert to UTC before formatting - the time value represents UTC time
  return calculatedDate.utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
}

export const getInitialDataForSettingDaysOfNextVisit = (
  nextDate,
  createdTime,
  nextVisitInputType = 'days',
) => {
  if (nextDate) {
    // Parse nextDate - it comes from server as UTC
    const nextDateMoment = dayjs.utc(nextDate)
    
    if (createdTime) {
      // Parse createdTime as UTC as well (server stores in UTC)
      const createdTimeMoment = dayjs.utc(createdTime, 'YYYY-MM-DD HH:mm:ss')
      return nextDateMoment.diff(createdTimeMoment, nextVisitInputType)
    } else {
      // Compare with current time in UTC
      return nextDateMoment.diff(dayjs.utc(), nextVisitInputType)
    }
  }
  return null
}

// latestvitalid in prescriptionData

// next visit
// threapy chosen
// therpast chosen from list
// therpast chosen manually
// new complaints fire api, id in complaints
// new diagnosis fire api, id in diagnosis
// QR : need something like this : https://chart.googleapis.com/chart?chs=150x150&cht=qr&chl=https://aumhum.com/mentor/aviral-pandey/events/talk-to-aviral

// decide what to do with save, print and save as it is taking a lot of time
// something to do with all these fields : newly created drug is not showing up
// if "genericName" coming, then show

// find solution 1: rerendering and slowing down fix - typing
// find solution 2:
